/**
 * Calendar dates without time or timezone ("YYYY-MM-DD").
 *
 * Deadlines are calendar dates in Portugal. Representing them as JS Date objects
 * invites off-by-one bugs around midnight and DST changes, so the domain works
 * with plain ISO date strings and does arithmetic in UTC at noon (DST-proof).
 */
export type DateOnly = string & { readonly __brand?: 'DateOnly' }

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/

export function isDateOnly(value: unknown): value is DateOnly {
  if (typeof value !== 'string') return false
  const m = ISO_DATE.exec(value)
  if (!m) return false
  const d = toUtc(value)
  return d.getUTCFullYear() === Number(m[1]) && d.getUTCMonth() + 1 === Number(m[2]) && d.getUTCDate() === Number(m[3])
}

function toUtc(value: DateOnly): Date {
  return new Date(`${value}T12:00:00Z`)
}

function fromUtc(date: Date): DateOnly {
  return date.toISOString().slice(0, 10)
}

export function dateOnly(year: number, month: number, day: number): DateOnly {
  return fromUtc(new Date(Date.UTC(year, month - 1, day, 12)))
}

export function parts(value: DateOnly): { year: number; month: number; day: number } {
  const m = ISO_DATE.exec(value)
  if (!m) throw new Error(`Data inválida: ${value}`)
  return { year: Number(m[1]), month: Number(m[2]), day: Number(m[3]) }
}

export function addDays(value: DateOnly, days: number): DateOnly {
  const d = toUtc(value)
  d.setUTCDate(d.getUTCDate() + days)
  return fromUtc(d)
}

/** Adds months clamping the day to the target month's length (31 Jan + 1 month = 28/29 Feb). */
export function addMonths(value: DateOnly, months: number): DateOnly {
  const { year, month, day } = parts(value)
  const targetIndex = year * 12 + (month - 1) + months
  const ty = Math.floor(targetIndex / 12)
  const tm = (targetIndex % 12) + 1
  return dateOnly(ty, tm, Math.min(day, daysInMonth(ty, tm)))
}

export function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate()
}

export function startOfMonth(value: DateOnly): DateOnly {
  const { year, month } = parts(value)
  return dateOnly(year, month, 1)
}

export function endOfMonth(value: DateOnly): DateOnly {
  const { year, month } = parts(value)
  return dateOnly(year, month, daysInMonth(year, month))
}

/** Whole days from `a` to `b` (b - a). */
export function diffDays(a: DateOnly, b: DateOnly): number {
  return Math.round((toUtc(b).getTime() - toUtc(a).getTime()) / 86_400_000)
}

export function compareDates(a: DateOnly, b: DateOnly): number {
  return a < b ? -1 : a > b ? 1 : 0
}

export function minDate(a: DateOnly, b: DateOnly): DateOnly {
  return a <= b ? a : b
}

export function maxDate(a: DateOnly, b: DateOnly): DateOnly {
  return a >= b ? a : b
}

/** ISO weekday: 1 = Monday … 7 = Sunday. */
export function isoWeekday(value: DateOnly): number {
  const dow = toUtc(value).getUTCDay()
  return dow === 0 ? 7 : dow
}

export function startOfIsoWeek(value: DateOnly): DateOnly {
  return addDays(value, 1 - isoWeekday(value))
}

/**
 * Business "today" in a timezone (default Europe/Lisbon). Never the browser's
 * timezone: a user travelling abroad must see the same deadlines as the office.
 */
export function todayIn(timeZone = 'Europe/Lisbon', now: Date = new Date()): DateOnly {
  const fmt = new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' })
  return fmt.format(now) as DateOnly
}

export function eachDay(from: DateOnly, to: DateOnly): DateOnly[] {
  const out: DateOnly[] = []
  for (let d = from; d <= to; d = addDays(d, 1)) out.push(d)
  return out
}
